/**
 * @file Album Folder Children component. Display a list of child album
 * folders and albums that the album folder may contain.
 */

import React, { Fragment, Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import AlbumFolderItem from './AlbumFolderItem';
import AlbumItem from './AlbumItem';
import {
  getSelectedAlbumData,
  shouldDisableAlbumForIdentity,
  shouldDisplayAlbumItem,
  shouldDisplayFolderItem,
} from '../../../util/discover-album-folder';
import {
  folderHasChildrenByTotals,
  getAlbumFolderChildAlbumFolders,
  getAlbumFolderChildAlbums,
} from '../../../util/album-folder';
import {
  getIdentityJoinedAlbumIds,
  identityHasAlbumMatches,
  isIdentityJoinedToAlbum,
  isIdentityPurchasedOnAlbum,
} from '../../../util/identity';
import Button from '../Button';
import Spinner from '../Spinner';
import { ALBUM_SUBTYPE } from '../../../constants/albums';
import ContextualString from '../../contextual/ContextualString';
import OnboardingLayoutScrollableContent from '../../../apps/temp/views/onboarding/OnboardingLayout/components/OnboardingLayoutScrollableContent';

class AlbumFolderChildren extends Component {
  static propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      children: PropTypes.shape({
        album_folders: PropTypes.shape({
          records: PropTypes.arrayOf(PropTypes.object),
        }),
        albums: PropTypes.shape({
          records: PropTypes.arrayOf(PropTypes.object),
        }),
      }),
      discovery_selection_info: PropTypes.string,
    }),
    basePath: PropTypes.string.isRequired,
    onSelectAlbum: PropTypes.func.isRequired,
    onDeselectAlbum: PropTypes.func.isRequired,
    albumFolderSelectedAlbums: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    identity: PropTypes.shape({
      name: PropTypes.string.isRequired,
      relationship: PropTypes.string.isRequired,
    }).isRequired,
    loading: PropTypes.bool.isRequired,
    allowAllSelection: PropTypes.bool,
  };

  constructor(props) {
    super(props);

    const { albumFolder } = this.props;
    const state = { albumFolders: [], albums: [] };

    // Handle SSR loading
    if (albumFolder) {
      const albumFolders = getAlbumFolderChildAlbumFolders(albumFolder, true);
      const albums = getAlbumFolderChildAlbums(albumFolder);

      state.albumFolders = albumFolders.filter(
        albumFolderItem =>
          folderHasChildrenByTotals(albumFolderItem) &&
          shouldDisplayFolderItem(albumFolderItem),
      );
      state.albums = albums.filter(shouldDisplayAlbumItem);
    }

    this.state = state;
  }

  // eslint-disable-next-line
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { albumFolder } = nextProps;

    // Handle client-side loading.
    if (this.props.albumFolder !== albumFolder) {
      const albumFolders = getAlbumFolderChildAlbumFolders(albumFolder, true);
      const albums = getAlbumFolderChildAlbums(albumFolder);

      this.setState(() => ({
        albumFolders: albumFolders.filter(
          albumFolderItem =>
            folderHasChildrenByTotals(albumFolderItem) &&
            shouldDisplayFolderItem(albumFolderItem),
        ),
        albums: albums.filter(shouldDisplayAlbumItem),
      }));
    }
  }

  onMultiSelectAlbums = selections => {
    const { albums } = this.state;
    const {
      albumFolderSelectedAlbums,
      albumFolder,
      onSelectAlbum,
      onDeselectAlbum,
    } = this.props;

    const albumSelectionIds = selections.map(({ value }) => value);
    const deselectedAlbums = albumFolderSelectedAlbums.filter(
      album => !albumSelectionIds.includes(album.id),
    );
    const selectedAlbumIds = albumFolderSelectedAlbums.map(({ id }) => id);

    deselectedAlbums.forEach(album => {
      const albumData = getSelectedAlbumData(album, albumFolder);
      onDeselectAlbum(albumData);
    });

    albumSelectionIds.forEach(id => {
      const album = albums.find(a => a.id === id);

      if (!selectedAlbumIds.includes(album.id)) {
        const albumData = getSelectedAlbumData(album, albumFolder);
        onSelectAlbum(albumData);
      }
    });
  };

  checkSelectAllAlbums = (albumFolderSelectedAlbums, albums, albumFolder) => {
    if (
      albumFolderSelectedAlbums.length === 0 &&
      albumFolder.album_subtype === ALBUM_SUBTYPE.YOUTH_SPORTS
    ) {
      albums.forEach(album => {
        const albumData = getSelectedAlbumData(album, albumFolder);
        this.props.onSelectAlbum(albumData);
      });
    }
  };

  selectAllAlbums = (albums, event) => {
    if (event) {
      event.preventDefault();
    }

    const {
      onSelectAlbum,
      albumFolder,
      albumFolderSelectedAlbums,
      identity,
    } = this.props;

    albums.forEach(album => {
      const isSelected = !!albumFolderSelectedAlbums.find(
        selectedAlbum => selectedAlbum.id === album.id,
      );

      const isPurchased = !isIdentityPurchasedOnAlbum(identity, album.id);

      if (
        !isSelected &&
        (isPurchased || !isIdentityJoinedToAlbum(identity, album.id)) &&
        !album.is_free
      ) {
        const albumData = getSelectedAlbumData(album, albumFolder);
        onSelectAlbum(albumData);
      }
    });
  };

  deselectAllAlbums = (albums, event) => {
    event.preventDefault();
    const { onDeselectAlbum } = this.props;

    albums.forEach(album => {
      onDeselectAlbum(album);
    });
  };

  displaySelectAllAlbums = () => {
    const { albums } = this.state;
    const { identity, albumFolderSelectedAlbums } = this.props;
    const joinedAlbumsIds = getIdentityJoinedAlbumIds(identity, true);
    const remainingAlbums = albums.filter(
      album => !joinedAlbumsIds.includes(album.id) && !album.is_free,
    );

    return remainingAlbums.length > albumFolderSelectedAlbums.length;
  };

  render() {
    const {
      albumFolder,
      basePath,
      onSelectAlbum,
      onDeselectAlbum,
      albumFolderSelectedAlbums,
      identity,
      loading,
      allowAllSelection,
    } = this.props;

    if (loading || !albumFolder) {
      return (
        <div className="flex-auto text-center mt-5">
          <Spinner />
        </div>
      );
    }

    const { albumFolders, albums } = this.state;

    return (
      <Fragment>
        {albums.length > 0 && allowAllSelection && (
          <div className="d-flex justify-content-between">
            <div className="text-right">
              {this.displaySelectAllAlbums() ? (
                <Button
                  btnStyle="link"
                  className="text-sm"
                  onClick={event => this.selectAllAlbums(albums, event)}
                >
                  Select all{' '}
                  <ContextualString
                    id="consumerAlbum"
                    subtype={albumFolder.album_subtype}
                    defaultMessage="album"
                    formatter={string => pluralize(string, 2)}
                  />
                </Button>
              ) : (
                <Button
                  btnStyle="link"
                  className="text-sm"
                  onClick={event => this.deselectAllAlbums(albums, event)}
                >
                  Deselect all{' '}
                  <ContextualString
                    id="consumerAlbum"
                    subtype={albumFolder.album_subtype}
                    defaultMessage="album"
                    formatter={string => pluralize(string, 2)}
                  />
                </Button>
              )}
            </div>
          </div>
        )}

        {albumFolder.discovery_selection_info && (
          <p className="pt-2">{albumFolder.discovery_selection_info}</p>
        )}
        <OnboardingLayoutScrollableContent className="w-100">
          <div className="discover-album-folder-list-container">
            {albumFolders.map(childAlbumFolder => (
              <AlbumFolderItem
                key={childAlbumFolder.id}
                albumFolder={childAlbumFolder}
                basePath={basePath}
              />
            ))}

            {albums.map(album => {
              const isSelected = !!albumFolderSelectedAlbums.find(
                selectedAlbum => selectedAlbum.id === album.id,
              );

              const isPurchased = !isIdentityPurchasedOnAlbum(
                identity,
                album.id,
              );

              return (
                <AlbumItem
                  key={album.id}
                  album={album}
                  albumFolder={albumFolder}
                  onSelectAlbum={onSelectAlbum}
                  onDeselectAlbum={onDeselectAlbum}
                  isSelected={isSelected}
                  isJoined={
                    !isPurchased && isIdentityJoinedToAlbum(identity, album.id)
                  }
                  hasMatchLink={
                    !isPurchased && identityHasAlbumMatches(identity, album.id)
                  }
                  disabled={shouldDisableAlbumForIdentity(
                    identity,
                    albums,
                    albumFolderSelectedAlbums,
                    album,
                  )}
                />
              );
            })}
          </div>
        </OnboardingLayoutScrollableContent>
      </Fragment>
    );
  }
}

export default AlbumFolderChildren;
